import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useSuspenseQuery } from '@apollo/client';
import {
  QUERY_FETCH_RESOURCE_HUB_LIST,
  QUERY_FETCH_RESOURCE_HUB_CATEGORIES,
  QUERY_FETCH_RESOURCE_HUB_CATEGORY_IDS_WITH_RESOURCES,
  type FetchResourceHubListQuery,
  type FetchResourceHubCategoriesQuery,
  type FetchResourceHubCategoryIdsWithResourcesQuery,
} from '@edwin-group/people-graphql';
import { useResourceHubListStore, type ResourceCategoryNode } from '../store/useResourceHubListStore';
import { useAuthentication } from '@/hooks/useAuthentication';

const INITIAL_PAGE_SIZE = 9;
const LOAD_MORE_PAGE_SIZE = 3;

type CategoryNode = NonNullable<NonNullable<FetchResourceHubCategoriesQuery['resourceCategories']>[number]>;

const mapCategoryNode = (node: CategoryNode): ResourceCategoryNode => ({
  documentId: node.documentId,
  category: node.category,
  color: node.color,
  subCategories: node.subCategories?.nodes?.filter((n): n is NonNullable<typeof n> => n !== null).map(mapCategoryNode),
});

const findCategoryById = (categories: ResourceCategoryNode[], id: string): ResourceCategoryNode | undefined => {
  for (const cat of categories) {
    if (cat.documentId === id) return cat;
    if (cat.subCategories?.length) {
      const found = findCategoryById(cat.subCategories, id);
      if (found) return found;
    }
  }
  return undefined;
};

const collectAllChildCategoryIds = (categories: ResourceCategoryNode[]): Set<string> => {
  const childIds = new Set<string>();
  const collectChildren = (cats: ResourceCategoryNode[]) => {
    cats.forEach((cat) => {
      if (cat.subCategories?.length) {
        cat.subCategories.forEach((sub) => {
          childIds.add(sub.documentId);
        });
        collectChildren(cat.subCategories);
      }
    });
  };
  collectChildren(categories);
  return childIds;
};

const collectDescendantIdsWithResources = (
  category: ResourceCategoryNode,
  categoryIdsWithResources: Set<string>,
): string[] => {
  const ids: string[] = [];
  if (categoryIdsWithResources.has(category.documentId)) {
    ids.push(category.documentId);
  }
  if (category.subCategories?.length) {
    for (const sub of category.subCategories) {
      ids.push(...collectDescendantIdsWithResources(sub, categoryIdsWithResources));
    }
  }
  return ids;
};

export const useResourceHubList = () => {
  const { schoolContext, authentication } = useAuthentication();
  const resources = useResourceHubListStore((state) => state.resources);
  const pagination = useResourceHubListStore((state) => state.pagination);
  const searchQuery = useResourceHubListStore((state) => state.searchQuery);
  const selectedCategoryIds = useResourceHubListStore((state) => state.selectedCategoryIds);
  const selectedSubcategoryIds = useResourceHubListStore((state) => state.selectedSubcategoryIds);
  const categories = useResourceHubListStore((state) => state.categories);
  const categoryIdsWithResources = useResourceHubListStore((state) => state.categoryIdsWithResources);

  const setResources = useResourceHubListStore((state) => state.setResources);
  const appendResources = useResourceHubListStore((state) => state.appendResources);
  const setCategories = useResourceHubListStore((state) => state.setCategories);
  const setRootCategories = useResourceHubListStore((state) => state.setRootCategories);
  const setCategoryIdsWithResources = useResourceHubListStore((state) => state.setCategoryIdsWithResources);
  const setPagination = useResourceHubListStore((state) => state.setPagination);
  const setLoading = useResourceHubListStore((state) => state.setLoading);

  const { data: categoriesData } = useSuspenseQuery<FetchResourceHubCategoriesQuery>(
    QUERY_FETCH_RESOURCE_HUB_CATEGORIES,
  );

  const { data: categoryIdsData } = useSuspenseQuery<FetchResourceHubCategoryIdsWithResourcesQuery>(
    QUERY_FETCH_RESOURCE_HUB_CATEGORY_IDS_WITH_RESOURCES,
    {
      variables: {
        schoolContextDocumentId: schoolContext?.documentId ?? '',
        userDocumentId: authentication?.user?.documentId ?? '',
      },
    },
  );

  useEffect(() => {
    if (categoryIdsData?.resources?.nodes) {
      const ids = new Set<string>();
      categoryIdsData.resources.nodes.forEach((node) => {
        if (node.category?.documentId) {
          ids.add(node.category.documentId);
        }
      });
      setCategoryIdsWithResources(ids);
    }
  }, [categoryIdsData, setCategoryIdsWithResources]);

  useEffect(() => {
    if (categoriesData?.resourceCategories) {
      const allCategories = categoriesData.resourceCategories
        .filter((node): node is CategoryNode => node !== null)
        .map(mapCategoryNode);
      setCategories(allCategories);

      const childIds = collectAllChildCategoryIds(allCategories);
      const roots = allCategories.filter((cat) => !childIds.has(cat.documentId));
      setRootCategories(roots);
    }
  }, [categoriesData, setCategories, setRootCategories]);

  const shouldFetch = true;

  const categoryDocumentIds = useMemo(() => {
    if (selectedCategoryIds.length === 0) return undefined;

    if (selectedSubcategoryIds.length === 0) {
      const ids = new Set<string>();
      for (const rootId of selectedCategoryIds) {
        const root = findCategoryById(categories, rootId);
        if (root) {
          collectDescendantIdsWithResources(root, categoryIdsWithResources).forEach((id) => ids.add(id));
        }
      }
      return ids.size > 0 ? Array.from(ids) : undefined;
    }

    const ids = new Set<string>();
    for (const subId of selectedSubcategoryIds) {
      const sub = findCategoryById(categories, subId);
      if (sub) {
        collectDescendantIdsWithResources(sub, categoryIdsWithResources).forEach((id) => ids.add(id));
      }
    }
    return ids.size > 0 ? Array.from(ids) : undefined;
  }, [selectedCategoryIds, selectedSubcategoryIds, categories, categoryIdsWithResources]);

  const {
    data: resourcesData,
    fetchMore,
    loading,
    refetch,
  } = useQuery<FetchResourceHubListQuery>(QUERY_FETCH_RESOURCE_HUB_LIST, {
    variables: {
      page: 1,
      pageSize: INITIAL_PAGE_SIZE,
      title: searchQuery || undefined,
      categoryDocumentIds,
      schoolContextDocumentId: schoolContext?.documentId ?? '',
      userDocumentId: authentication?.user?.documentId ?? '',
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (resourcesData?.resources) {
      const nodes = resourcesData.resources.nodes || [];
      const pageInfo = resourcesData.resources.pageInfo;

      setResources(nodes);

      if (pageInfo) {
        setPagination({
          page: pageInfo.page || 1,
          pageCount: pageInfo.pageCount || 1,
          total: pageInfo.total || 0,
          pageSize: pageInfo.pageSize || INITIAL_PAGE_SIZE,
        });
      }
    }
  }, [resourcesData, setResources, setPagination]);

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    refetch({
      page: 1,
      pageSize: INITIAL_PAGE_SIZE,
      title: searchQuery || undefined,
      categoryDocumentIds,
      schoolContextDocumentId: schoolContext?.documentId ?? '',
      userDocumentId: authentication?.user?.documentId ?? '',
    });
  }, [categoryDocumentIds, searchQuery, refetch, schoolContext?.documentId, authentication?.user?.documentId]);

  const loadMore = useCallback(async () => {
    if (resources.length >= pagination.total) return;

    const nextPage = Math.floor(resources.length / LOAD_MORE_PAGE_SIZE) + 1;
    setLoading(true);
    const { data } = await fetchMore({
      variables: {
        page: nextPage,
        pageSize: LOAD_MORE_PAGE_SIZE,
        title: searchQuery || undefined,
        categoryDocumentIds,
        schoolContextDocumentId: schoolContext?.documentId ?? '',
        userDocumentId: authentication?.user?.documentId ?? '',
      },
    });

    if (data?.resources) {
      const nodes = data.resources.nodes || [];
      const pageInfo = data.resources.pageInfo;

      appendResources(nodes);

      if (pageInfo) {
        setPagination({
          page: pageInfo.page || nextPage,
          pageCount: pageInfo.pageCount || pagination.pageCount,
          total: pageInfo.total || pagination.total,
          pageSize: pageInfo.pageSize || LOAD_MORE_PAGE_SIZE,
        });
      }
    }
    setLoading(false);
  }, [
    resources.length,
    pagination.total,
    pagination.pageCount,
    fetchMore,
    searchQuery,
    categoryDocumentIds,
    schoolContext?.documentId,
    authentication?.user?.documentId,
    appendResources,
    setPagination,
    setLoading,
  ]);

  const hasMore = resources.length < pagination.total;

  return {
    loading,
    loadMore,
    hasMore,
    shouldFetch,
  };
};
